import { useState, type ChangeEvent, type DragEvent } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Field } from '@/components/common/Field'
import { primaryButtonClass, selectClass } from '@/components/common/formStyles'
import { listAccounts } from '@/lib/accountsApi'

const MAX_BYTES = 10 * 1024 * 1024
const SUPPORTED_BROKERS = ['ibkr'] as const

interface SourceCardProps {
  accountId: number | null
  file: File | null
  onAccountChange: (id: number | null) => void
  onFileChange: (f: File | null) => void
  onSubmit: () => void
  disabled?: boolean
  isLoading?: boolean
}

function validateFile(f: File): string | null {
  if (!f.name.toLowerCase().endsWith('.csv')) return 'Nur .csv erlaubt'
  if (f.size > MAX_BYTES) return 'Datei zu groß (max 10 MB)'
  return null
}

export function SourceCard({
  accountId,
  file,
  onAccountChange,
  onFileChange,
  onSubmit,
  disabled = false,
  isLoading = false,
}: SourceCardProps) {
  const [fileError, setFileError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts })

  const importableAccounts =
    accountsQuery.data?.filter((a) =>
      (SUPPORTED_BROKERS as readonly string[]).includes(a.broker_type),
    ) ?? []

  function handleFile(f: File | null) {
    if (!f) {
      setFileError(null)
      onFileChange(null)
      return
    }
    const err = validateFile(f)
    if (err) {
      setFileError(err)
      onFileChange(null)
      return
    }
    setFileError(null)
    onFileChange(f)
  }

  function handleSelect(e: ChangeEvent<HTMLInputElement>) {
    handleFile(e.target.files?.[0] ?? null)
  }

  function handleDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    setIsDragging(false)
    if (disabled) return
    handleFile(e.dataTransfer.files?.[0] ?? null)
  }

  function handleDragOver(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    if (!disabled) setIsDragging(true)
  }

  function handleDragLeave(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    setIsDragging(false)
  }

  if (accountsQuery.isLoading) {
    return (
      <section className="hairline border bg-panel p-6 text-sm text-muted">Lade Accounts …</section>
    )
  }

  if (importableAccounts.length === 0) {
    return (
      <section className="hairline border bg-panel p-6">
        <p className="mb-3 text-sm text-ink">Für CSV-Imports brauchst du einen IBKR-Account.</p>
        <Link to="/positions" className={primaryButtonClass}>
          Account anlegen
        </Link>
      </section>
    )
  }

  const dropZoneClass = [
    'flex h-24 cursor-pointer flex-col items-center justify-center gap-1 border border-dashed text-sm transition-colors md:h-32',
    isDragging ? 'border-bernstein bg-bernstein/10' : 'border-hairline bg-void',
    disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-bernstein',
  ].join(' ')

  const submitDisabled = disabled || isLoading || accountId === null || file === null

  return (
    <section className="hairline flex flex-col gap-4 border bg-panel p-4 md:p-6">
      <h2 className="tracked-caps text-xs text-muted">Quelle wählen</h2>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Account">
          <select
            value={accountId ?? ''}
            onChange={(e) => onAccountChange(e.target.value === '' ? null : Number(e.target.value))}
            disabled={disabled}
            className={selectClass}
          >
            <option value="">— Bitte wählen —</option>
            {importableAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Datei (CSV, max 10 MB)">
          <label
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={dropZoneClass}
          >
            <span className="text-muted">
              {file ? file.name : 'Datei hier ablegen oder klicken'}
            </span>
            <input
              type="file"
              accept=".csv"
              onChange={handleSelect}
              disabled={disabled}
              className="sr-only"
            />
          </label>
          {fileError && <p className="mt-1 text-xs text-loss">{fileError}</p>}
        </Field>
      </div>

      <button
        type="button"
        onClick={onSubmit}
        disabled={submitDisabled}
        className={primaryButtonClass + ' w-full md:w-auto md:self-start'}
      >
        {isLoading ? 'Lade Preview …' : 'Preview anzeigen'}
      </button>
    </section>
  )
}
